import { useEffect, useState } from 'react';
import { Gift, LogIn, LogOut, Settings, ShoppingBag, Trophy, UserRound } from 'lucide-react';
import { useAuth } from '../app/auth.ts';
import { useT } from '../app/preferences.ts';
import { Dialog } from './Dialog.tsx';
import { Link } from './Link.tsx';
import { GoldAmount, PlayerAvatar, PlayerName } from './PlayerLook.tsx';

/**
 * Header: "Sign in" when signed out. Signed in: gold (opens the shop), a gift
 * marker when boxes are waiting, and the player's avatar (opens the account card).
 */
export function AccountButton({ onOpenSettings }: { onOpenSettings: () => void }) {
  const t = useT();
  const { me, user, canSignIn, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const notice = useLoginNotice();

  return (
    <>
      {user?.username ? (
        <>
          <Link href="/market" className="btn btn-sm gold-chip" aria-label={t('gold.amount', { amount: user.gold })}>
            <GoldAmount amount={user.gold} />
          </Link>
          {user.boxes > 0 && (
            <Link href="/gifts" className="btn btn-sm gift-chip" aria-label={t('nav.gifts')}>
              <Gift size={20} />
              <span className="gift-count">{user.boxes}</span>
            </Link>
          )}
          <button type="button" className="btn btn-sm account-btn" onClick={() => setOpen(true)} aria-label={t('account.title')}>
            <PlayerAvatar look={user.look} name={user.username} size="sm" />
            <span className="account-rating-chip">🏆 {user.rating}</span>
            <span className="header-label account-name">{user.username}</span>
            {user.boxes > 0 && <span className="account-dot" aria-hidden="true" />}
          </button>
        </>
      ) : !user && canSignIn ? (
        <button type="button" className="btn btn-sm btn-blue" onClick={() => signIn()}>
          <LogIn size={20} />
          <span>{me?.login.google ? t('auth.signInShort') : t('auth.devSignIn')}</span>
        </button>
      ) : null}

      {user?.username && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          title={t('account.title')}
        >
          <div className="account-card">
            <PlayerAvatar look={user.look} name={user.username} size="lg" />
            <div className="account-name-lg">
              <PlayerName name={user.username} look={user.look} />
            </div>
            <span className="badge badge-accent">
              <GoldAmount amount={user.gold} />
            </span>
            <div className="account-rating">
              <span className="emoji" aria-hidden="true">
                🏆
              </span>{' '}
              {t('account.rating')}: <strong>{user.rating}</strong>
            </div>
            <dl className="account-stats">
              <div>
                <dt>{t('account.games')}</dt>
                <dd>{user.games}</dd>
              </div>
              <div>
                <dt>{t('account.wins')}</dt>
                <dd>{user.wins}</dd>
              </div>
              <div>
                <dt>{t('account.draws')}</dt>
                <dd>{user.draws}</dd>
              </div>
              <div>
                <dt>{t('account.losses')}</dt>
                <dd>{user.losses}</dd>
              </div>
            </dl>
            <nav className="account-links" onClick={() => setOpen(false)}>
              <Link href="/me" className="btn btn-blue">
                <UserRound size={20} /> {t('nav.profile')}
              </Link>
              <Link href="/market" className="btn btn-primary">
                <ShoppingBag size={20} /> {t('nav.market')}
              </Link>
              <Link href="/gifts" className="btn">
                <Gift size={20} /> {t('nav.gifts')}
                {user.boxes > 0 && <span className="gift-count">{user.boxes}</span>}
              </Link>
              <Link href="/leaderboard" className="btn">
                <Trophy size={20} /> {t('nav.leaderboard')}
              </Link>
              <button type="button" className="btn" onClick={onOpenSettings}>
                <Settings size={20} /> {t('nav.settings')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => void signOut()}>
                <LogOut size={20} /> {t('auth.signOut')}
              </button>
            </nav>
          </div>
        </Dialog>
      )}

      {notice && (
        <div className="toast-region" aria-live="polite">
          <div className="toast">{t(notice === 'failed' ? 'auth.failed' : 'auth.unavailable')}</div>
        </div>
      )}
    </>
  );
}

/** The server sends ?login=failed|unavailable back after a sign-in that did not work. */
function useLoginNotice(): 'failed' | 'unavailable' | null {
  const [notice, setNotice] = useState<'failed' | 'unavailable' | null>(() => {
    const value = new URLSearchParams(window.location.search).get('login');
    return value === 'failed' || value === 'unavailable' ? value : null;
  });

  useEffect(() => {
    if (!notice) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('login');
    window.history.replaceState(null, '', url.pathname + url.search);
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  return notice;
}
